import React from 'react';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material';
import CryptoJS from 'crypto-js';
import JSZip from 'jszip';
import QRCode from 'qrcode';
import _ from 'lodash';
import i18n from '../../i18n';
import apiManage, { apiManage_rbp } from '../../request';
import { getRBPStringId } from '../main/main-page-states';
import { parseRestaurantStaticBundled } from '../../utils/restaurant-static-bundled';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import CommonInput from '../../component/form/common-input.component';
import PasswordInput from '../../component/form/password-input';
import type { SelectOption } from '../../component/form/base-input.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import type { QopSetting } from '../../models/type.constant';
import { SectionTable } from '../../models/section-table.entity';
import { drawCanvas, QopCanvasWeb, QopCanvasWifi } from '../../constants/qop-image-draw';
import QOP_TEMPLATE from '../../static/qop-code-templates';
import { getQopTempById, getTempTheme } from '../../static/qop-code-templates/types';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import LSM from '../../modules/cache/tool-local-storage';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import AvatarCard from '../../component/avatar/avatar-card';
import AvatarDialog from '../../component/avatar/avatar-dialog';
import { PreviewTemplateForQop } from './qop-preview-template.component';
import { ReactComponent as FreeBreakfastIcon } from '../../static/images/svg/free_breakfast.svg';
import { ReactComponent as VisibilityIcon } from '../../static/images/svg/material-symbols-light_visibility.svg';
import { ReactComponent as DownloadIcon } from '../../static/images/svg/material-symbols_download-rounded.svg';
import { ReactComponent as PrintIcon } from '../../static/images/svg/material-symbols-light_print-rounded.svg';
import { ReactComponent as QrBatchIcon } from '../../static/images/svg/bx_qr.svg';
import { ReactComponent as SearchRoundedIcon } from '../../static/images/svg/material-symbols_search-rounded.svg';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import SubFunctionProjectSettingSaveButton from './sub-function-project-setting-save-button.component';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import { QopSettingPageSkeleton } from './sub-function-settings-skeletons.component';
import './scss/qop-setting.page.scss';
import { IMAGE_UPLOAD_SPECS } from '../../utils/image-upload-spec';

/** 公网 QOP 相关授权码，与 RBP2 `QOP_AUTH`、RWA 页 `QOP_WARRANT_CODES` 一致 */
const QOP_WARRANT_CODES = ['AU1024', 'AU1025', 'AU1026'] as const;

/** 云端暂不支持打印；恢复外带/逐桌「打印」入口时改为 `true` */
const QOP_CLOUD_PRINT_ENABLED = false;

/**
 * QOP 页顶栏「辅助模式」开启时在各区块标题下展示的 Figma 说明 i18n key（与 KWA `KWA_AUX_HINT_I18N_KEYS` 语义一致；多处共用同一句）。
 * 新增稿面辅助句时补入本数组并在对应位置接 `SubFunctionAuxiliaryHint`。
 */
const QOP_AUX_HINT_I18N_KEYS = ['figma_aux_project_url_example'] as const;

/**
 * 从本地缓存读取静态资源根地址，与 MOP 公告图等页一致。
 * @returns 服务器根 URL（可能为空字符串）
 */
function readServerUrlBase(): string {
    const raw = LSM.get(LOCAL_STORAGE_KEYS.SERVER_URL);
    return typeof raw === 'string' ? raw.replace(/\/+$/, '') : '';
}

/**
 * 浏览器空闲时调度重任务，与 RBP2 `isRequestIdleCallback` 等价。
 * @param task - 待执行任务
 */
function scheduleIdleTask(task: () => void): void {
    const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void };
    if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(task, { timeout: 2000 });
    } else {
        setTimeout(task, 0);
    }
}

/** Shark id=100 表单，与后端 `set` 字段一致（不含生成用语 `language`，与 RBP2 行为一致） */
type QopSharkForm = QopSetting;

type Props = Record<string, never>;

/** 基础设置行：与 `getSettingById` / PATCH 语义对齐 */
type QopBasicRow = {
    /** 设置项 id */
    key: number;
    /** 展示形态 */
    type: 'switch' | 'number';
    /** 当前值 */
    value: boolean | number;
    /** 主标题 i18n key（Figma 1901:23578 subtitle2） */
    labelKey: string;
    /** 辅助模式说明 i18n key（稿面次要文案 body2，仅顶栏「辅助模式」开启时展示） */
    descKey: string;
};

type State = {
    /** 全页保存中 */
    isSaving: boolean;
    /** 基础开关与数值项 */
    settingArr: QopBasicRow[];
    /** 二维码表单（shark id=100），与后端 `set` 字段一致 */
    proQopSetting: QopSharkForm;
    /** 合成桌码图时的界面语言，与 RBP2 `state.language` 一致（不落 shark） */
    qopLanguage: string;
    /** 扫码生成方式（设置 id 155）：1 内网 / 2 外网 / 3 全部 */
    codeType: number;
    /** 是否具备公网 QOP 授权（控制 155 的 2、3 可选与公网地址展示） */
    showPublicUrl: boolean;
    /** 公网扫码点餐根地址（由 Smartway 校验结果拼装） */
    publicUrlDisplay: string;
    /** 桌台列表，用于桌台二维码区 */
    tableList: SectionTable[];
    /** 清空桌台历史临时密码确认框 */
    alertOpen: boolean;
    /** 单张预览图 base64 */
    previewUrl: string;
    /** 模板版式预览弹窗 */
    isPreviewDialog: boolean;
    /** 是否开启点餐地址加密（桌台 id 走 AES） */
    isEncryptTable: boolean;
    /** 首包拉取完成前展示骨架 */
    initialPageDataReady: boolean;
    /** 桌台二维码区：是否展开本地搜索输入（Figma 8112:32448） */
    tableSearchOpen: boolean;
    /** 桌台名称前端筛选关键字（trim 后子串匹配，不请求接口） */
    tableSearchKeyword: string;
};

/**
 * 构建初始基础设置行，顺序与 Figma 1901:23578「基础设置」列表一致（与旧 RBP2 顺序不同处以设计稿为准）。
 * @returns 初始行配置
 */
function buildInitialBasicRows(): QopBasicRow[] {
    return [
        {
            key: 37,
            type: 'switch',
            value: false,
            labelKey: 'qop_setting_page_send_kitchen_print',
            descKey: 'qop_setting_page_send_kitchen_print_desc',
        },
        {
            key: 123,
            type: 'switch',
            value: false,
            labelKey: 'qop_setting_page_take_out_print',
            descKey: 'qop_setting_page_take_out_print_desc',
        },
        {
            key: 98,
            type: 'switch',
            value: false,
            labelKey: 'qop_setting_page_order_address_encryption',
            descKey: 'qop_setting_page_order_address_encryption_desc',
        },
        {
            key: 88,
            type: 'number',
            value: 4,
            labelKey: 'qop_setting_page_scan_interval',
            descKey: 'qop_setting_page_scan_interval_desc',
        },
        {
            key: 72,
            type: 'switch',
            value: false,
            labelKey: 'qop_setting_page_temporary_pin',
            descKey: 'qop_setting_page_temporary_pin_desc',
        },
        {
            key: 1,
            type: 'switch',
            value: false,
            labelKey: 'qop_setting_page_set_map',
            descKey: 'qop_setting_page_set_map_desc',
        },
        {
            key: 2,
            type: 'number',
            value: 0,
            labelKey: 'qop_setting_page_map_scope',
            descKey: 'qop_setting_page_map_scope_desc',
        },
    ];
}

/**
 * 默认 QOP 二维码表单，与 RBP2 `defualtQopSetting` 对齐。
 * @returns 默认表单对象
 */
function buildDefaultQopForm(): QopSharkForm {
    return {
        webUrl: '',
        wifiName: '',
        wifiPassword: '',
        // 与 RBP2 `defualtQopSetting.wifiSecurity` 一致，默认 WPA
        wifiSecurity: 'WPA',
        templateId: 1,
        tempTheme: 'light',
    };
}

/**
 * 云 RBP「子功能管理」— QOP 设置：平迁 RBP2 `project-setting-qop`（基础项、模板、桌台码、批量下载），走 `apiManage_rbp`。
 * 隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class QopSettingPage extends React.Component<Props, State> {
    /** 顶栏「辅助模式」：控制 `QOP_AUX_HINT_I18N_KEYS` 对应 Figma 次要说明（与 DIS/KWA 等子页一致） */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    /** 防止保存重复提交 */
    private saveLocked = false;

    /** 内置 8 套 + 自定义模板图 URL，与 RBP2 `qopTemplate` 一致 */
    private qopTemplate: Record<string, string> = { ...QOP_TEMPLATE };

    /** 餐厅名称，用于合成桌码图 */
    private restaurantName = '';

    /** 合成用底图地址 */
    private templateUrl = '';

    /** 合成用 WiFi 块 */
    private wifiBlock: QopCanvasWifi = { type: 'wifi', name: '', password: '', security: 'None' };

    /** 主题 token 映射 */
    private temThemesMap = JSON.parse(getTempTheme(getQopTempById(1)));

    public constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            settingArr: buildInitialBasicRows(),
            proQopSetting: buildDefaultQopForm(),
            qopLanguage: 'IT',
            codeType: 1,
            showPublicUrl: false,
            publicUrlDisplay: '',
            tableList: [],
            alertOpen: false,
            previewUrl: '',
            isPreviewDialog: false,
            isEncryptTable: false,
            initialPageDataReady: false,
            tableSearchOpen: false,
            tableSearchKeyword: '',
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.loadPageData();
    }

    /**
     * 合并云端自定义 QOP 模板图列表：仅依据 `GET .../restaurant-static/bundled-image-urls` 返回中的 `qopMould` 条目构建 `template{id}` → 预览 URL；无条目时仅保留内置 `QOP_TEMPLATE`。
     */
    private setQopTemps = async (): Promise<void> => {
        const base = readServerUrlBase();
        /** 由 bundled 解析出的模板 id → 可访问图 URL，覆盖内置同 id 预览 */
        const extra: Record<string, string> = {};
        const sid = getRBPStringId();
        const bundledRaw = await apiManage.get_restaurant_static_bundled_image_urls(sid);
        const qopEntries = parseRestaurantStaticBundled(bundledRaw).qopMouldEntries;
        if (qopEntries.length > 0) {
            for (const entry of qopEntries) {
                const s = String(entry || '').trim();
                if (!s) {
                    continue;
                }
                const fileMatch = s.match(/(\d+)\.[a-z0-9]+$/i);
                const idFromName = fileMatch ? fileMatch[1] : '';
                if (idFromName) {
                    const url = /^https?:\/\//i.test(s) ? s : `${base}/qopMould/${s}?randomId=${Math.random()}`;
                    _.set(extra, `template${idFromName}`, url);
                }
            }
        }
        this.qopTemplate = {
            ...QOP_TEMPLATE,
            ...extra,
        };
    };

    /**
     * 拉取桌台列表，与 RBP2 `getResTables` 一致。
     * @returns 过滤掉无 `table_id` 的项
     */
    private getResTables = async (): Promise<SectionTable[]> => {
        const result = await apiManage_rbp.get_res_tables({});
        if (!_.get(result, 'error')) {
            return ((result || []) as SectionTable[]).filter((item) => item.table_id) || [];
        }
        GlobalNotification.error(i18n.t('global_text_operate_failure'));
        return [];
    };

    /**
     * 拉取基础设置、shark 100、155、桌台、模板图及公网授权信息。
     */
    private loadPageData = async (): Promise<void> => {
        const baseRows = buildInitialBasicRows();
        try {
        const showPublicUrl = await this.refreshPublicQopAuth();
        const [
            res37,
            res72,
            res88,
            res98,
            res123,
            res155,
            mapRes,
            gmapRes,
            res100,
            tableList,
            restaurant,
        ] = await Promise.all([
            apiManage_rbp.getSettingById(37),
            apiManage_rbp.getSettingById(72),
            apiManage_rbp.getSettingById(88),
            apiManage_rbp.getSettingById(98),
            apiManage_rbp.getSettingById(123),
            apiManage_rbp.getSettingById(155),
            apiManage_rbp.get_settings_map_address(),
            apiManage_rbp.get_google_map(),
            apiManage_rbp.getSettingById(100),
            this.getResTables(),
            apiManage_rbp.get_restaurant_info(),
        ]);

        let isEncryptTable = false;
        const nextRows = baseRows.map((row) => {
            const next = { ...row };
            switch (row.key) {
                case 37:
                    next.value = _.get(res37, '[0].value.enable') ? true : false;
                    break;
                case 72:
                    next.value = Boolean(Number(_.get(res72, '[0].value', 0) || 0));
                    break;
                case 88:
                    next.value = Number(_.get(res88, '[0].value', 4) || 4);
                    break;
                case 98:
                    next.value = Boolean(Number(_.get(res98, '[0].value', 0) || 0));
                    isEncryptTable = Boolean(Number(_.get(res98, '[0].value', 0) || 0));
                    break;
                case 123:
                    next.value = Boolean(Number(_.get(res123, '[0].value', 0) || 0));
                    break;
                case 1:
                    next.value = _.get(gmapRes, 'set_map') === '1';
                    break;
                case 2:
                    next.value = Number(_.get(mapRes, 'map_scope', 0) || 0);
                    break;
                default:
                    break;
            }
            return next;
        });

        let proQopSetting = buildDefaultQopForm();
        let qopLanguage = 'IT';
        if (!_.get(res100, 'error')) {
            const raw = _.get(res100, '[0].value') || {};
            const merged: QopSharkForm = { ...proQopSetting, ...raw };
            const ws = String(merged.wifiSecurity || 'None');
            // 与 RBP2 一致：历史或云端存 WPA/WPA2 时归一为 WPA；旧版 Shell2 的 WEP 也并入 WPA
            if (ws === 'WPA/WPA2' || ws === 'WEP') {
                merged.wifiSecurity = 'WPA';
            }
            const langRaw = _.get(raw, 'language');
            if (typeof langRaw === 'string' && langRaw) {
                qopLanguage = langRaw;
            }
            proQopSetting = merged;
        }

        this.restaurantName = String(_.get(restaurant, 'restaurant_name', '') || '');
        await this.setQopTemps();
        const templateKeyCount = Object.keys(this.qopTemplate).length;
        if (proQopSetting.templateId > templateKeyCount) {
            proQopSetting = { ...proQopSetting, templateId: 1 };
        }

        let codeType = Number(_.get(res155, '[0].value', 1) || 1);
        if (!showPublicUrl && (codeType === 2 || codeType === 3)) {
            codeType = 1;
        }

        this.setState({
            settingArr: nextRows,
            proQopSetting,
            qopLanguage,
            codeType,
            showPublicUrl,
            tableList,
            isEncryptTable,
        });
        } finally {
            this.setState({ initialPageDataReady: true });
        }
    };

    /**
     * 判断是否具备公网 QOP 能力，并拼装公网地址展示串。
     * @returns 是否具备公网授权
     */
    private refreshPublicQopAuth = async (): Promise<boolean> => {
        let show = false;
        let publicUrlDisplay = '';
        const api45 = await apiManage_rbp.getSettingById(45);
        const swCloudKey = String(_.get(api45, '[0].value', '') || '').trim();
        if (swCloudKey) {
            const verifyRes = await apiManage_rbp.getSmartwayCloudKey(swCloudKey);
            const stringId = _.get(verifyRes, ['restaurant', 'string_id']);
            if (stringId && !_.get(verifyRes, 'error')) {
                publicUrlDisplay = `http://${stringId}.qop.evophotic.it/#/`;
                const data = await apiManage_rbp.getAuthorizationMessage();
                const authorizationMessage = _.get(data, 'warrant_data', '');
                if (_.isArray(authorizationMessage) && authorizationMessage.length > 0) {
                    show = !!authorizationMessage.find((item: { code?: string }) =>
                        QOP_WARRANT_CODES.includes(item?.code as (typeof QOP_WARRANT_CODES)[number]),
                    );
                }
            }
        }
        this.setState({ publicUrlDisplay });
        return show;
    };

    /**
     * WiFi 密码：加密方式非 None 时至少 8 位。
     * @param wifiPassword - 密码明文
     * @param wifiSecurity - 加密方式
     * @returns 是否通过校验
     */
    private validateQopWifiPassword(wifiPassword: string, wifiSecurity: string): boolean {
        if (wifiSecurity === 'None') {
            return true;
        }
        if (String(wifiPassword ?? '').trim().length < 8) {
            GlobalNotification.error(i18n.t('qop_setting_page_wifi_password_min_length'));
            return false;
        }
        return true;
    }

    /**
     * 持久化基础设置与 shark id=100。
     */
    private persistSettings = async (): Promise<void> => {
        const { settingArr, proQopSetting, codeType } = this.state;
        const { webUrl, wifiName, wifiPassword, wifiSecurity } = proQopSetting;
        const verifyIpPortReg =
            /^(http:\/\/)(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])(:([0-9]|[1-9]\d{1,3}|[1-5]\d{4}|6[0-5]{2}[0-3][0-5]))?\/#\/$/;
        const trimmedUrl = String(webUrl || '').trim();
        const urlOk = verifyIpPortReg.test(trimmedUrl);
        if (!urlOk || !trimmedUrl) {
            GlobalNotification.error(i18n.t('qop_setting_page_project_newwork_address_warning_info'));
            return;
        }
        if (!(wifiName && (wifiPassword || wifiSecurity === 'None') && wifiSecurity)) {
            GlobalNotification.error(i18n.t('qop_setting_page_please_wifi_info_incomplete'));
            return;
        }
        if (!this.validateQopWifiPassword(wifiPassword, wifiSecurity)) {
            return;
        }

        setLoading(true);
        let error = false;
        let nextIsEncryptTable = this.state.isEncryptTable;
        for (const row of settingArr) {
            switch (row.key) {
                case 37: {
                    const r = await apiManage_rbp.patchSettingById(37, { enable: !!row.value });
                    if (_.get(r, 'error')) {
                        error = true;
                    }
                    break;
                }
                case 72: {
                    const r = await apiManage_rbp.patch_order_head_password_on_off(row.value ? '1' : '0');
                    if (_.get(r, 'error')) {
                        error = true;
                    }
                    break;
                }
                case 1: {
                    const r = await apiManage_rbp.patch_google_map(row.value ? '1' : '0');
                    if (_.get(r, 'error')) {
                        error = true;
                    }
                    break;
                }
                case 2: {
                    const r = await apiManage_rbp.patch_settings_map_address({ map_scope: Number(row.value) });
                    if (_.get(r, 'error')) {
                        error = true;
                    }
                    break;
                }
                case 88: {
                    const r = await apiManage_rbp.patch_qop_scan_time_interval(Number(row.value));
                    if (_.get(r, 'error')) {
                        GlobalNotification.error(String(_.get(r, 'result', i18n.t('global_text_operate_failure'))));
                        error = true;
                        setLoading(false);
                        return;
                    }
                    break;
                }
                case 98: {
                    const r = await apiManage_rbp.patchSettingById(98, { stringValue: String(Number(!!row.value)) });
                    nextIsEncryptTable = Boolean(row.value);
                    if (_.get(r, 'error')) {
                        error = true;
                    }
                    break;
                }
                case 123: {
                    const r = await apiManage_rbp.patchSettingById(123, { stringValue: String(Number(!!row.value)) });
                    if (_.get(r, 'error')) {
                        error = true;
                    }
                    break;
                }
                default:
                    break;
            }
        }

        const setPayload: QopSharkForm = {
            ...proQopSetting,
            webUrl: trimmedUrl,
        };
        const res100 = await apiManage_rbp.post_update_shark_settings({
            id: 100,
            set: setPayload,
        });
        if (_.get(res100, 'error')) {
            error = true;
        }

        const res155 = await apiManage_rbp.patchSettingById(155, { stringValue: Number(codeType) });
        if (_.get(res155, 'error')) {
            error = true;
        }

        if (error) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            this.setState({ isEncryptTable: nextIsEncryptTable });
        }
        setLoading(false);
    };

    /**
     * 清空桌台历史临时密码，与 RBP2 `delPassword` 一致。
     */
    private delPassword = async (): Promise<void> => {
        this.setState({ alertOpen: false });
        const result = await apiManage_rbp.post_order_head_password_del();
        if (!_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    /**
     * 上传自定义 QOP 模板图。
     * @param file - 图片文件
     * @param tempId - 模板序号
     * @returns 是否成功
     */
    private updateQopTempImage = async (file: File, tempId: number): Promise<boolean> => {
        if (!file) {
            return false;
        }
        const imageResult = await apiManage_rbp.post_qop_temp_image(String(tempId), file);
        return !_.get(imageResult, 'error');
    };

    /**
     * 桌台重名时追加序号，避免 zip 内文件名冲突（与 RBP2 一致）。
     * @param table - 名称与 dataURL 列表
     * @returns 可能改写 `name` 后的同一引用数组
     */
    private checkTableRepeat(table: { name: string; data: string }[]): { name: string; data: string }[] {
        const nameList: string[] = [];
        table.forEach((item) => {
            const name = item?.name || '';
            if (nameList.includes(name)) {
                let newName = name;
                for (let i = 1; nameList.includes(newName); i += 1) {
                    newName = `${name}(${i})`;
                }
                nameList.push(newName);
                item.name = newName;
            } else {
                nameList.push(item.name);
            }
        });
        return table;
    }

    /**
     * 批量下载为 zip（单层目录），与 RBP2 `handleBatchDownload` 一致。
     */
    private handleBatchDownload = async (selectImgList: { name: string; data: string }[], zipName?: string): Promise<void> => {
        const zip = new JSZip();
        selectImgList.forEach((item) => {
            zip.file(`${item.name}.png`, item.data.replace(/^data:image\/(png|jpg|jpeg);base64,/, ''), { base64: true });
        });
        const content = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        link.download = zipName || 'qr-code.zip';
        link.href = window.URL.createObjectURL(content);
        link.click();
        setLoading(false);
    };

    /**
     * 批量下载为 zip（公网/内网分子目录），与 RBP2 `handleBatchFolderDownload` 一致。
     */
    private handleBatchFolderDownload = async (selectImgList: { name: string; data: string }[], zipName?: string): Promise<void> => {
        const zip = new JSZip();
        const publicImages: { name: string; data: string }[] = [];
        const privateImages: { name: string; data: string }[] = [];
        selectImgList.forEach((item) => {
            if (item.name?.includes('public')) {
                const cleaned = { ...item, name: item.name.replace('public-', '') };
                publicImages.push(cleaned);
            } else {
                privateImages.push(item);
            }
        });
        const publicFolder = zip.folder('Data network');
        const privateFolder = zip.folder('Intranet');
        publicImages.forEach((item) => {
            publicFolder?.file(`${item.name}.png`, item.data.replace(/^data:image\/(png|jpg|jpeg);base64,/, ''), { base64: true });
        });
        privateImages.forEach((item) => {
            privateFolder?.file(`${item.name}.png`, item.data.replace(/^data:image\/(png|jpg|jpeg);base64,/, ''), { base64: true });
        });
        const content = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = zipName ? `${zipName}.zip` : 'download.zip';
        link.click();
        setLoading(false);
    };

    /**
     * 单张预览或下载，与 RBP2 `preview` 一致。
     */
    private preview = async (type: 'GEN' | 'LOOK', result: { name: string; data: string }): Promise<void> => {
        const { name = '', data = '' } = result;
        setLoading(false);
        if (type === 'LOOK') {
            this.setState({ previewUrl: data });
            return;
        }
        const link = document.createElement('a');
        link.download = `${name || 'qr-code'}.jpg`;
        link.href = data;
        link.click();
    };

    /**
     * 合成并预览/下载桌码图，与 RBP2 `handleCodeParam` 一致。
     * @param type - LOOK 预览 / GEN 下载
     * @param tables - 目标桌台列表
     */
    private handleCodeParam = async (type: 'GEN' | 'LOOK', tables: SectionTable[]): Promise<void> => {
        const { proQopSetting, isEncryptTable, codeType, qopLanguage } = this.state;
        const { wifiName, wifiPassword, wifiSecurity, tempTheme, webUrl, templateId } = proQopSetting;
        if (codeType !== 2 && !String(webUrl || '').trim()) {
            GlobalNotification.error(i18n.t('qop_setting_page_please_type_QR_code_info'));
            return;
        }
        if (!(wifiName && (wifiPassword || wifiSecurity === 'None') && wifiSecurity) && codeType !== 2) {
            GlobalNotification.error(i18n.t('qop_setting_page_please_wifi_info_incomplete'));
            return;
        }
        if (codeType !== 2 && !this.validateQopWifiPassword(wifiPassword, wifiSecurity)) {
            return;
        }
        if (!templateId) {
            GlobalNotification.error(i18n.t('qop_setting_page_select_waring_message'));
            return;
        }
        try {
            setLoading(true);
            let tempId = 0;
            if (templateId <= 8) {
                tempId = templateId;
            } else if (tempTheme === 'light') {
                tempId = -1;
            } else if (tempTheme === 'dark') {
                tempId = -2;
            }
            const keys = Object.keys(this.qopTemplate);
            this.templateUrl = this.qopTemplate[keys[templateId - 1]];
            const temIdMap = getQopTempById(tempId);
            this.temThemesMap = JSON.parse(getTempTheme(temIdMap));
            this.wifiBlock = {
                type: 'wifi',
                name: wifiName,
                password: wifiPassword,
                security: wifiSecurity,
            };
            const webs: QopCanvasWeb[] = [];
            const publicRoot = String(this.state.publicUrlDisplay || '').trim();
            tables.forEach((item) => {
                const url = codeType === 2 ? publicRoot : String(webUrl || '');
                const encryptTableId = CryptoJS.AES.encrypt(`${item.table_id || ''}`, 'TableID').toString();
                const tableId = codeType === 2 || isEncryptTable ? encryptTableId : item.table_id!;
                webs.push({
                    type: 'web',
                    url,
                    tableId,
                    tableName: `${item.table_name!}`,
                    publicUrl: codeType === 3 ? publicRoot : '',
                    publicTableId: codeType === 3 ? encryptTableId : '',
                });
            });
            const pngList = await drawCanvas(
                this.templateUrl,
                this.temThemesMap,
                this.wifiBlock,
                webs,
                this.restaurantName,
                codeType,
                qopLanguage,
            );
            if (tables.length === 1) {
                await this.preview(type, pngList[0]);
            } else if (tables.length > 1 && type === 'GEN') {
                await this.handleBatchDownload(pngList);
            }
            setLoading(false);
        } catch (e) {
            console.log(e);
            setLoading(false);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    /**
     * 批量生成纯二维码 zip（WiFi + 每桌 URL），与 RBP2 `genTableQrCode` 一致。
     */
    private genTableQrCode = async (): Promise<void> => {
        try {
            const { proQopSetting, isEncryptTable, tableList, codeType } = this.state;
            const { wifiName, wifiPassword, wifiSecurity, webUrl } = proQopSetting;
            if (!webUrl && codeType !== 2) {
                GlobalNotification.error(i18n.t('qop_setting_page_please_type_QR_code_info'));
                return;
            }
            if (!(wifiName && (wifiPassword || wifiSecurity === 'None') && wifiSecurity) && codeType !== 2) {
                GlobalNotification.error(i18n.t('qop_setting_page_please_wifi_info_incomplete'));
                return;
            }
            if (codeType !== 2 && !this.validateQopWifiPassword(wifiPassword, wifiSecurity)) {
                return;
            }
            setLoading(true);
            scheduleIdleTask(async () => {
                const webs: { name: string; data: string }[] =
                    codeType === 2
                        ? []
                        : [
                              {
                                  name: 'WIFI',
                                  data: await QRCode.toDataURL(
                                      wifiSecurity === 'None'
                                          ? `WIFI:S:${wifiName};T:nopass;P:;`
                                          : `WIFI:T:${wifiSecurity};S:${wifiName};P:${wifiPassword};`,
                                      { width: 400 },
                                  ),
                              },
                          ];
                const publicRoot = String(this.state.publicUrlDisplay || '').trim();
                for (const item of tableList) {
                    const tableId =
                        codeType === 2 || isEncryptTable
                            ? CryptoJS.AES.encrypt(`${item.table_id || ''}`, 'TableID').toString()
                            : item.table_id!;
                    const requestUrl = codeType === 2 ? publicRoot : String(webUrl || '');
                    const url = await QRCode.toDataURL(`${requestUrl}?table_id=${tableId}`, { width: 400 });
                    webs.push({
                        name: `${item.table_name!}`,
                        data: url,
                    });
                    if (codeType === 3) {
                        const publicTableId = CryptoJS.AES.encrypt(`${item.table_id || ''}`, 'TableID').toString();
                        const publicUrl = await QRCode.toDataURL(`${publicRoot}?table_id=${publicTableId}`, { width: 400 });
                        webs.push({
                            name: `public-${item.table_name!}`,
                            data: publicUrl,
                        });
                    }
                }
                const newWebs = this.checkTableRepeat(webs);
                if (codeType === 3) {
                    await this.handleBatchFolderDownload(newWebs, 'table-qrCode');
                } else {
                    await this.handleBatchDownload(newWebs, 'table-qrCode');
                }
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
            setLoading(false);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    /**
     * 打印单桌二维码，与 RBP2 `printCode` 一致。
     */
    private printCode = async (table: SectionTable): Promise<void> => {
        try {
            const { proQopSetting, isEncryptTable, codeType } = this.state;
            const { webUrl, wifiName, wifiPassword, wifiSecurity } = proQopSetting;
            if (codeType !== 2 && !webUrl) {
                GlobalNotification.error(i18n.t('qop_setting_page_please_type_QR_code_info'));
                return;
            }
            if (codeType !== 2 && !(wifiName && (wifiPassword || wifiSecurity === 'None') && wifiSecurity)) {
                GlobalNotification.error(i18n.t('qop_setting_page_please_wifi_info_incomplete'));
                return;
            }
            if (codeType !== 2 && !this.validateQopWifiPassword(wifiPassword, wifiSecurity)) {
                return;
            }
            setLoading(true);
            let isSuccess = true;
            const publicRoot = String(this.state.publicUrlDisplay || '').trim();
            if ([1, 3].includes(codeType)) {
                const qrCodeData = {
                    web: {
                        type: 'web',
                        url: webUrl,
                        tableId: isEncryptTable
                            ? CryptoJS.AES.encrypt(`${table.table_id || ''}`, 'TableID').toString()
                            : table.table_id!,
                        tableName: `${table.table_name!}`,
                    },
                    wifi: {
                        type: 'wifi',
                        name: wifiName,
                        password: wifiPassword,
                        security: wifiSecurity,
                    },
                };
                const res = await apiManage_rbp.post_table_qr_code_print(qrCodeData);
                if (_.get(res, 'error') || (res as { ok?: boolean })?.ok === false) {
                    const errMsg = _.get(res, 'errorList[0]');
                    GlobalNotification.error(errMsg || i18n.t('qop_setting_page_scan_by_network_print_failed'));
                    isSuccess = false;
                }
            }
            if ([2, 3].includes(codeType)) {
                const publicCodeData = {
                    web: {
                        type: 'web',
                        url: publicRoot,
                        tableId: CryptoJS.AES.encrypt(`${table.table_id || ''}`, 'TableID').toString(),
                        tableName: `${table.table_name!}`,
                    },
                };
                const res = await apiManage_rbp.post_table_qr_code_print(publicCodeData);
                if (_.get(res, 'error') || (res as { ok?: boolean })?.ok === false) {
                    const errMsg = _.get(res, 'errorList[0]');
                    GlobalNotification.error(errMsg || i18n.t('qop_setting_page_scan_by_wifi_print_failed'));
                    isSuccess = false;
                }
            }
            setLoading(false);
            if (isSuccess) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            }
        } catch (e) {
            console.log(e);
            setLoading(false);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    /** QOP Lite 公网下载（防抖入口），与 RBP2 `downloadLite` 一致 */
    private downloadLiteDebounced = _.debounce((): void => {
        void this.downloadLiteInner();
    }, 200);

    /**
     * 下载 QOP Lite WAN 示意码图。
     */
    private downloadLiteInner = async (): Promise<void> => {
        try {
            const { proQopSetting, qopLanguage } = this.state;
            const { tempTheme, templateId, wifiName, wifiPassword, wifiSecurity } = proQopSetting;
            this.wifiBlock = {
                type: 'wifi',
                name: wifiName,
                password: wifiPassword,
                security: wifiSecurity,
            };
            setLoading(true);
            let tempId = 0;
            if (templateId <= 8) {
                tempId = templateId;
            } else if (tempTheme === 'light') {
                tempId = -1;
            } else if (tempTheme === 'dark') {
                tempId = -2;
            }
            const keys = Object.keys(this.qopTemplate);
            this.templateUrl = this.qopTemplate[keys[templateId - 1]];
            this.temThemesMap = JSON.parse(getTempTheme(getQopTempById(tempId)));
            const publicRoot = String(this.state.publicUrlDisplay || '').trim();
            const webs: QopCanvasWeb[] = [
                {
                    type: 'web',
                    url: `${publicRoot}lite`,
                    tableId: '',
                    tableName: 'Lite',
                },
            ];
            const pngList = await drawCanvas(
                this.templateUrl,
                this.temThemesMap,
                this.wifiBlock,
                webs,
                this.restaurantName,
                2,
                qopLanguage,
            );
            await this.preview('GEN', pngList[0]);
        } catch (e) {
            console.log(e);
            setLoading(false);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    /**
     * 点击保存。
     */
    private onClickSave = async (): Promise<void> => {
        if (this.saveLocked || this.state.isSaving) {
            return;
        }
        this.saveLocked = true;
        this.setState({ isSaving: true });
        try {
            await this.persistSettings();
        } finally {
            this.saveLocked = false;
            this.setState({ isSaving: false });
        }
    };

    /**
     * 修改基础设置行。
     * @param val - 新值
     * @param key - 设置 id
     */
    private onChangeBasic = (val: boolean | number, key: number): void => {
        const settingArr = this.state.settingArr.map((n) => {
            if (n.key !== key) {
                return n;
            }
            if (key === 2) {
                const num = Number(val);
                return { ...n, value: Number.isFinite(num) && num < 0 ? 0 : num };
            }
            if (key === 88) {
                const num = Number(val);
                const v = !Number.isFinite(num) || num < 1 ? 1 : Math.floor(num);
                return { ...n, value: v };
            }
            return { ...n, value: val };
        });
        this.setState({ settingArr });
    };

    /**
     * 修改扫码方式（id 155），与 RBP2 一致立即 PATCH。
     * @param next - 1 | 2 | 3
     */
    private onChangeCodeType = async (next: number): Promise<void> => {
        if ((next === 2 || next === 3) && !this.state.showPublicUrl) {
            GlobalNotification.error(i18n.t('cop_setting_page_qr_code_scan_error'));
            return;
        }
        this.setState({ codeType: next });
        const r = await apiManage_rbp.patchSettingById(155, { stringValue: Number(next) });
        if (_.get(r, 'error')) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    /**
     * 更新二维码表单字段。
     * @param patch - 部分字段
     */
    private patchQopForm = (patch: Partial<QopSharkForm>): void => {
        this.setState({ proQopSetting: { ...this.state.proQopSetting, ...patch } });
    };

    /**
     * WiFi 加密下拉项（`label` 为展示文案，非 i18n key）。
     * @returns 下拉选项
     */
    private getWifiSecurityItems(): SelectOption[] {
        // 与 RBP2 `project-setting-qop` 中 wifiSecurity 下拉 `items` 一致（仅 None、WPA）
        return [
            { value: 'None', label: 'None' },
            { value: 'WPA', label: 'WPA' },
        ];
    }

    /**
     * 扫码方式下拉项（`label` 为 i18n key，由 BaseInput 翻译）。
     * @returns 下拉选项
     */
    private getCodeTypeItems(): SelectOption[] {
        const items: SelectOption[] = [
            { value: '1', label: 'qop_setting_page_local_network' },
            { value: '2', label: 'qop_setting_page_data_network', disabled: !this.state.showPublicUrl },
            { value: '3', label: 'qop_setting_page_scan_mode_intranet_extranet', disabled: !this.state.showPublicUrl },
        ];
        return items;
    }

    /**
     * 二维码语言下拉项。
     * @returns 下拉选项
     */
    private getLanguageItems(): SelectOption[] {
        return ['IT', 'ZH', 'EN', 'ES', 'FR', 'CA'].map((code) => ({
            value: code,
            label: code,
        }));
    }

    /**
     * 展开/收起桌台本地搜索条；收起时清空关键字以恢复全列表。
     */
    private onToggleTableSearch = (): void => {
        this.setState((prev) => {
            const nextOpen = !prev.tableSearchOpen;
            return {
                tableSearchOpen: nextOpen,
                tableSearchKeyword: nextOpen ? prev.tableSearchKeyword : '',
            };
        });
    };

    /**
     * 按桌台名称前端过滤（不区分大小写，trim 后子串匹配 `table_name`）。
     * @param list - 接口返回的全量桌台
     * @param keyword - 用户输入
     * @returns 仅用于展示的子集
     */
    private filterTablesByKeyword(list: SectionTable[], keyword: string): SectionTable[] {
        const q = keyword.trim().toLowerCase();
        if (!q) {
            return list;
        }
        return list.filter((row) => String(row.table_name ?? '').toLowerCase().includes(q));
    }

    public render(): React.ReactNode {
        if (!this.state.initialPageDataReady) {
            return <QopSettingPageSkeleton />;
        }
        const {
            isSaving,
            settingArr,
            proQopSetting,
            codeType,
            showPublicUrl,
            publicUrlDisplay,
            tableList,
            alertOpen,
            previewUrl,
            isPreviewDialog,
            qopLanguage,
            tableSearchOpen,
            tableSearchKeyword,
        } = this.state;
        /** 桌台列表经本地关键字过滤后的结果，仅影响列表展示 */
        const filteredTableList = this.filterTablesByKeyword(tableList, tableSearchKeyword);
        /** 与壳页顶栏「辅助模式」同步，为 true 时展示 `QOP_AUX_HINT_I18N_KEYS` 文案 */
        const auxiliaryModeEnabled = !!this.context?.auxiliaryModeEnabled;
        const wifiSecuritySelect = String(proQopSetting.wifiSecurity || 'None');
        const templateKeys = Object.keys(this.qopTemplate);
        const customSlotIndex = templateKeys.length + 1;
        return (
            <Box className="qop-setting-page">
                <Box className="qop-setting-page__surface">
                    <Box className="qop-setting-page__header">
                        <Typography variant="h4" color="primary" className="qop-setting-page__page-title">
                            {i18n.t('qop_setting_page_heading')}
                        </Typography>
                        <SubFunctionProjectSettingSaveButton loading={isSaving} onClick={this.onClickSave} />
                    </Box>

                    <Grid container spacing={2} className="qop-setting-page__columns">
                        <Grid item xs={12} lg={6}>
                            <Box className="qop-setting-page__card qop-setting-page__card--column">
                                <Typography variant="subtitle1" color="primary" className="qop-setting-page__section-title">
                                    {i18n.t('qop_setting_page_basic_section_title')}
                                </Typography>
                                <Stack spacing={1}>
                                    {settingArr.map((row) => (
                                        <Box key={row.key} className="qop-setting-page__row">
                                            <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="subtitle2" color="text.primary">
                                                        {i18n.t(row.labelKey)}
                                                    </Typography>
                                                    <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey={row.descKey} />
                                                </Stack>
                                            </Box>
                                            <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                                                {row.type === 'switch' ? (
                                                    <IOSSwitch
                                                        checked={!!row.value}
                                                        onChange={() => this.onChangeBasic(!row.value, row.key)}
                                                        inputProps={{ 'aria-label': i18n.t(row.labelKey) }}
                                                    />
                                                ) : row.key === 88 || row.key === 2 ? (
                                                    <Box
                                                        className={
                                                            row.key === 88
                                                                ? 'qop-setting-page__metric-input-wrap qop-setting-page__metric-input-wrap--hours'
                                                                : 'qop-setting-page__metric-input-wrap'
                                                        }
                                                    >
                                                        <CommonInput
                                                            inputType="INPUT"
                                                            type="number"
                                                            size="small"
                                                            fullWidth={false}
                                                            className="qop-setting-page__metric-input-field"
                                                            value={String(row.value ?? '')}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                this.onChangeBasic(Number(e.target.value), row.key)
                                                            }
                                                        />
                                                        <Typography
                                                            variant="subtitle2"
                                                            color="text.secondary"
                                                            component="span"
                                                            className="qop-setting-page__metric-suffix"
                                                        >
                                                            {row.key === 88
                                                                ? i18n.t('qop_setting_page_metric_suffix_hours')
                                                                : i18n.t('qop_setting_page_metric_suffix_meters')}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <CommonInput
                                                        inputType="INPUT"
                                                        type="number"
                                                        size="small"
                                                        value={String(row.value ?? '')}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            this.onChangeBasic(Number(e.target.value), row.key)
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                                <Box className="qop-setting-page__clear-password">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => this.setState({ alertOpen: true })}
                                        className="qop-setting-page__clear-password-btn"
                                    >
                                        <Typography variant="subtitle1" component="span" sx={{ color: 'inherit' }}>
                                            {i18n.t('qop_setting_page_clear_table_history_temporary_password')}
                                        </Typography>
                                    </Button>
                                </Box>

                                <Box className="qop-setting-page__section-block qop-setting-page__section-block--before-grid">
                                    <Typography variant="subtitle2" color="text.primary">
                                        {i18n.t('qop_setting_page_QR_code_setup')}
                                    </Typography>
                                    <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey={QOP_AUX_HINT_I18N_KEYS[0]} />
                                </Box>
                                {/*
                                 * 与上方「二维码设置」`section-block` 同为水平 8px inset；两行 Stack 代替 Grid spacing，避免负边距与标题左缘错位。
                                 */}
                                <Stack
                                    spacing={2}
                                    className="qop-setting-page__qr-network-fields"
                                    sx={{ width: '100%', px: '8px', boxSizing: 'border-box' }}
                                >
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <Box sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field qop-setting-page__field--surface-input">
                                            <Typography variant="body2" color="text.primary" className="qop-setting-page__field-label">
                                                {i18n.t('qop_setting_page_project_url')}
                                            </Typography>
                                            <CommonInput
                                                inputType="INPUT"
                                                size="small"
                                                fullWidth
                                                placeholder={i18n.t('qop_setting_page_project_website_example_httpIP')}
                                                value={proQopSetting.webUrl || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    this.patchQopForm({ webUrl: e.target.value })
                                                }
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field qop-setting-page__field--surface-input">
                                            <Typography variant="body2" color="text.primary" className="qop-setting-page__field-label">
                                                {i18n.t('qop_setting_page_wifi_security_encryption')}
                                            </Typography>
                                            <CommonInput
                                                inputType="SELECT"
                                                size="small"
                                                fullWidth
                                                items={this.getWifiSecurityItems()}
                                                value={wifiSecuritySelect}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const v = e.target.value as QopSharkForm['wifiSecurity'];
                                                    this.patchQopForm({
                                                        wifiSecurity: v,
                                                        ...(v === 'None' ? { wifiPassword: '' } : {}),
                                                    });
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <Box sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field qop-setting-page__field--surface-input">
                                            <Typography variant="body2" color="text.primary" className="qop-setting-page__field-label">
                                                {i18n.t('qop_setting_page_wifi_name')}
                                            </Typography>
                                            <CommonInput
                                                inputType="INPUT"
                                                size="small"
                                                fullWidth
                                                value={proQopSetting.wifiName || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    this.patchQopForm({ wifiName: e.target.value })
                                                }
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field qop-setting-page__field--surface-input">
                                            <Typography variant="body2" color="text.primary" className="qop-setting-page__field-label">
                                                {i18n.t('qop_setting_page_wifi_password')}
                                            </Typography>
                                            {/* 复用公共 PasswordInput：右侧眼睛切换明文/密文；校验由本页 `validateQopWifiPassword` 等负责 */}
                                            <PasswordInput
                                                className=""
                                                size="small"
                                                noValidate
                                                disabled={wifiSecuritySelect === 'None'}
                                                value={proQopSetting.wifiPassword || ''}
                                                onChange={(value) => this.patchQopForm({ wifiPassword: value })}
                                            />
                                        </Box>
                                    </Stack>
                                </Stack>

                                <Box className="qop-setting-page__template-toolbar">
                                    <Typography variant="subtitle1" color="primary" className="qop-setting-page__section-title">
                                        {i18n.t('qop_setting_page_template_select')}
                                    </Typography>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                row
                                                value={proQopSetting.tempTheme || 'light'}
                                                onChange={(_, v) => this.patchQopForm({ tempTheme: v as QopSetting['tempTheme'] })}
                                            >
                                                <FormControlLabel
                                                    value="light"
                                                    control={<Radio color="primary" />}
                                                    label={<Typography variant="body2">{i18n.t('qop_setting_page_light_color')}</Typography>}
                                                />
                                                <FormControlLabel
                                                    value="dark"
                                                    control={<Radio color="primary" />}
                                                    label={<Typography variant="body2">{i18n.t('qop_setting_page_dark_color')}</Typography>}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                        <Button variant="outlined" color="primary" onClick={() => this.setState({ isPreviewDialog: true })}>
                                            {i18n.t('qop_setting_page_preview_template')}
                                        </Button>
                                    </Stack>
                                </Box>
                                <Box className="qop-setting-page__template-strip">
                                    {templateKeys.map((item: string, index: number) => (
                                        <Box
                                            key={item}
                                            className={
                                                proQopSetting.templateId === index + 1
                                                    ? 'qop-setting-page__template-thumb qop-setting-page__template-thumb--active'
                                                    : 'qop-setting-page__template-thumb'
                                            }
                                            onClick={() => this.patchQopForm({ templateId: index + 1 })}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    this.patchQopForm({ templateId: index + 1 });
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            {index < 8 ? (
                                                <AvatarCard
                                                    avatarUrl={_.get(this.qopTemplate, item)}
                                                    uploadEmptyVariant="firstScreenGallery"
                                                    uploadSpec={IMAGE_UPLOAD_SPECS.qopTemplate}
                                                    slotWidth={100}
                                                    slotHeight={160}
                                                    emptyUploadLabel={i18n.t('qop_setting_page_upload_template')}
                                                />
                                            ) : (
                                                <AvatarCard
                                                    avatarUrl={_.get(this.qopTemplate, item)}
                                                    uploadEmptyVariant="firstScreenGallery"
                                                    uploadSpec={IMAGE_UPLOAD_SPECS.qopTemplate}
                                                    slotWidth={100}
                                                    slotHeight={160}
                                                    emptyUploadLabel={i18n.t('qop_setting_page_upload_template')}
                                                    onChangeAvatarUrl={async (_url, file) => {
                                                        const tempId = Number(item.split('template')[1]) || -1;
                                                        const upRes = await this.updateQopTempImage(file, tempId);
                                                        if (upRes) {
                                                            await this.setQopTemps();
                                                            this.setState({});
                                                        }
                                                    }}
                                                    onDelete={async () => {
                                                        const tempId = Number(item.split('template')[1]) || -1;
                                                        const deRes = await apiManage_rbp.delete_qop_temp_image(tempId);
                                                        if (!_.get(deRes, 'error')) {
                                                            await this.setQopTemps();
                                                            this.patchQopForm({ templateId: 1 });
                                                            this.setState({});
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                    <Box
                                        className={
                                            proQopSetting.templateId === customSlotIndex
                                                ? 'qop-setting-page__template-thumb qop-setting-page__template-thumb--active'
                                                : 'qop-setting-page__template-thumb'
                                        }
                                    >
                                        <AvatarCard
                                            uploadEmptyVariant="firstScreenGallery"
                                            uploadSpec={IMAGE_UPLOAD_SPECS.qopTemplate}
                                            slotWidth={100}
                                            slotHeight={160}
                                            emptyUploadLabel={i18n.t('qop_setting_page_upload_template')}
                                            onChangeAvatarUrl={async (_url, file) => {
                                                const len = templateKeys.length;
                                                const res = await this.updateQopTempImage(file, len + 1);
                                                if (res) {
                                                    _.set(this.qopTemplate, `template${len + 1}`, URL.createObjectURL(file));
                                                    await this.setQopTemps();
                                                    this.patchQopForm({ templateId: len + 1 });
                                                    this.setState({});
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <Box className="qop-setting-page__card qop-setting-page__card--column qop-setting-page__card--right-rail-root">
                                {/*
                                 * 右侧栏顺序对齐 Figma 1900:22975：二维码设置 → 公网扫码地址 → 扫码方式/语言 → 外卖打包单 → 桌台二维码（工具条+列表）
                                 */}
                                <Stack spacing={3} className="qop-setting-page__right-rail" sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <Box className="qop-setting-page__section-block">
                                        <Typography variant="subtitle2" color="text.primary">
                                            {i18n.t('qop_setting_page_QR_code_setup')}
                                        </Typography>
                                        <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey={QOP_AUX_HINT_I18N_KEYS[0]} />
                                    </Box>
                                    {showPublicUrl && publicUrlDisplay ? (
                                        <>
                                            <Box className="qop-setting-page__section-block">
                                                <Typography variant="subtitle2" color="text.primary">
                                                    {i18n.t('qop_setting_page_public_url')}
                                                </Typography>
                                                <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey={QOP_AUX_HINT_I18N_KEYS[0]} />
                                            </Box>
                                            <Box className="qop-setting-page__public-url-row">
                                                <Box className="qop-setting-page__field qop-setting-page__field--surface-input qop-setting-page__public-url-field">
                                                    <CommonInput inputType="INPUT" size="small" fullWidth disabled value={publicUrlDisplay} />
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className="qop-setting-page__lite-wan-btn"
                                                    onClick={() => this.downloadLiteDebounced()}
                                                >
                                                    <Typography variant="subtitle1" component="span" sx={{ color: 'inherit' }}>
                                                        {i18n.t('qop_setting_page_lite_wan_button')}
                                                    </Typography>
                                                </Button>
                                            </Box>
                                        </>
                                    ) : null}
                                    {/*
                                     * 使用 Stack + 与 section-block 相同的水平 8px，避免 MUI Grid spacing 负边距导致与「外卖打包单」标题左缘不齐。
                                     */}
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={3}
                                        className="qop-setting-page__scan-lang-row"
                                        sx={{ width: '100%', px: '8px', boxSizing: 'border-box' }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field qop-setting-page__field--surface-input">
                                            <Typography variant="body2" color="text.primary" className="qop-setting-page__field-label">
                                                {i18n.t('qop_setting_page_scan_type')}
                                            </Typography>
                                            <CommonInput
                                                inputType="SELECT"
                                                size="small"
                                                fullWidth
                                                items={this.getCodeTypeItems()}
                                                value={String(codeType)}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    void this.onChangeCodeType(Number(e.target.value))
                                                }
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }} className="qop-setting-page__field qop-setting-page__field--surface-input">
                                            <Typography variant="body2" color="text.primary" className="qop-setting-page__field-label">
                                                {i18n.t('qop_setting_page_language_label')}
                                            </Typography>
                                            <CommonInput
                                                inputType="SELECT"
                                                size="small"
                                                fullWidth
                                                items={this.getLanguageItems()}
                                                value={qopLanguage}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    this.setState({ qopLanguage: e.target.value })
                                                }
                                            />
                                        </Box>
                                    </Stack>
                                    {/*
                                     * 标题与「查看 / 下载」同一行、顶部对齐；右侧两枚按钮保持一行（nowrap）。
                                     */}
                                    <Stack
                                        direction="row"
                                        alignItems="flex-start"
                                        justifyContent="space-between"
                                        spacing={2}
                                        className="qop-setting-page__takeaway-row"
                                        flexWrap="wrap"
                                        sx={{ width: '100%', px: '8px', boxSizing: 'border-box' }}
                                    >
                                        <Typography variant="subtitle2" color="text.primary" sx={{ flex: '1 1 auto', minWidth: 0, pr: 1 }}>
                                            {i18n.t('qop_setting_page_packet_list')}
                                        </Typography>
                                        <Stack direction="row" spacing={2} flexWrap="nowrap" sx={{ flexShrink: 0 }}>
                                            <Button
                                                className="qop-setting-page__segment-action"
                                                variant="outlined"
                                                color="primary"
                                                aria-label={i18n.t('qop_setting_page_takeaway_view')}
                                                onClick={() =>
                                                    void this.handleCodeParam('LOOK', [
                                                        { table_id: -1, table_name: 'TAKE AWAY' } as SectionTable,
                                                    ])
                                                }
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ width: '100%' }}
                                                    spacing={1}
                                                >
                                                    <SvgIcon component={VisibilityIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                    <Typography variant="body2" component="span">
                                                        {i18n.t('qop_setting_page_takeaway_view')}
                                                    </Typography>
                                                </Stack>
                                            </Button>
                                            <Button
                                                className="qop-setting-page__segment-action"
                                                variant="outlined"
                                                color="primary"
                                                aria-label={i18n.t('qop_setting_page_takeaway_download')}
                                                onClick={() =>
                                                    void this.handleCodeParam('GEN', [
                                                        { table_id: -1, table_name: 'TAKE AWAY' } as SectionTable,
                                                    ])
                                                }
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ width: '100%' }}
                                                    spacing={1}
                                                >
                                                    <SvgIcon component={DownloadIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                    <Typography variant="body2" component="span">
                                                        {i18n.t('qop_setting_page_takeaway_download')}
                                                    </Typography>
                                                </Stack>
                                            </Button>
                                        </Stack>
                                    </Stack>
                                    <Box
                                        className="qop-setting-page__table-rail"
                                        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
                                    >
                                        <Box className="qop-setting-page__section-block">
                                            <Typography variant="subtitle2" color="text.primary">
                                                {i18n.t('qop_setting_page_table_qr_title')}
                                            </Typography>
                                            <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey={QOP_AUX_HINT_I18N_KEYS[0]} />
                                        </Box>
                                        <Stack spacing={1} className="qop-setting-page__table-toolbar-panel" sx={{ width: '100%' }}>
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                className="qop-setting-page__table-toolbar"
                                                flexWrap="wrap"
                                                sx={{ width: '100%', justifyContent: 'flex-end' }}
                                            >
                                                <Button
                                                    className="qop-setting-page__toolbar-chip qop-setting-page__toolbar-chip--search"
                                                    variant="outlined"
                                                    color="primary"
                                                    aria-pressed={tableSearchOpen}
                                                    aria-label={i18n.t('general_text_search')}
                                                    onClick={this.onToggleTableSearch}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        sx={{ width: '100%' }}
                                                    >
                                                        <SvgIcon component={SearchRoundedIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                        <Typography variant="body2" component="span">
                                                            {i18n.t('general_text_search')}
                                                        </Typography>
                                                    </Stack>
                                                </Button>
                                                <Button
                                                    className="qop-setting-page__toolbar-chip"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => void this.handleCodeParam('GEN', tableList)}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                        <SvgIcon component={DownloadIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                        <SvgIcon component={FreeBreakfastIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                        <Typography variant="body2" component="span">
                                                            {i18n.t('qop_setting_page_batch_generation')}
                                                        </Typography>
                                                    </Stack>
                                                </Button>
                                                <Button
                                                    className="qop-setting-page__toolbar-chip"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => void this.genTableQrCode()}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                        <SvgIcon component={DownloadIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                        <SvgIcon component={QrBatchIcon} inheritViewBox sx={{ fontSize: 24 }} aria-hidden />
                                                        <Typography variant="body2" component="span">
                                                            {i18n.t('qop_setting_page_batch_generation_tables_code')}
                                                        </Typography>
                                                    </Stack>
                                                </Button>
                                            </Stack>
                                            {tableSearchOpen ? (
                                                <Box className="qop-setting-page__table-search-field">
                                                    <CommonInput
                                                        inputType="INPUT"
                                                        size="small"
                                                        fullWidth
                                                        placeholder={i18n.t('qop_setting_page_table_search_placeholder')}
                                                        value={tableSearchKeyword}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            this.setState({ tableSearchKeyword: e.target.value })
                                                        }
                                                    />
                                                </Box>
                                            ) : null}
                                        </Stack>
                                        <Box className="qop-setting-page__table-list">
                                    {filteredTableList.length === 0 && tableList.length > 0 ? (
                                        <Box className="qop-setting-page__table-list-empty">
                                            <Typography variant="body2" color="text.secondary">
                                                {i18n.t('qop_setting_page_table_search_empty')}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                    {filteredTableList.map((row) => (
                                        <Box key={String(row.table_id)} className="qop-setting-page__table-row">
                                            <Stack direction="row" spacing={2} alignItems="center" className="qop-setting-page__table-row-main">
                                                <SvgIcon
                                                    component={FreeBreakfastIcon}
                                                    inheritViewBox
                                                    className="qop-setting-page__table-row-icon"
                                                    aria-hidden
                                                />
                                                <Typography variant="body1" color="text.primary" component="span">
                                                    {i18n.t('qop_setting_page_table_row_prefix')}
                                                </Typography>
                                                <Typography variant="subtitle1" color="text.primary" component="span">
                                                    {row.table_name || ''}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <IconButton
                                                    color="primary"
                                                    aria-label={i18n.t('customers_page_look_over')}
                                                    onClick={() => void this.handleCodeParam('LOOK', [row])}
                                                >
                                                    <SvgIcon component={VisibilityIcon} inheritViewBox sx={{ fontSize: 32 }} />
                                                </IconButton>
                                                <IconButton
                                                    color="primary"
                                                    aria-label={i18n.t('qop_setting_page_generate')}
                                                    onClick={() => void this.handleCodeParam('GEN', [row])}
                                                >
                                                    <SvgIcon component={DownloadIcon} inheritViewBox sx={{ fontSize: 32 }} />
                                                </IconButton>
                                                {QOP_CLOUD_PRINT_ENABLED ? (
                                                    <IconButton
                                                        color="primary"
                                                        aria-label={i18n.t('qop_setting_page_print')}
                                                        onClick={() => void this.printCode(row)}
                                                    >
                                                        <SvgIcon component={PrintIcon} inheritViewBox sx={{ fontSize: 32 }} />
                                                    </IconButton>
                                                ) : null}
                                            </Stack>
                                        </Box>
                                    ))}
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {alertOpen ? (
                    <AlertDialog
                        open={alertOpen}
                        title={i18n.t('qop_setting_page_confirm_clear_table_history_temporary_password')}
                        desc={i18n.t('qop_setting_page_can_not_recover_whether_sure')}
                        onCancel={() => this.setState({ alertOpen: false })}
                        onConfirm={this.delPassword}
                    />
                ) : null}

                {previewUrl ? (
                    <AvatarDialog
                        open={!!previewUrl}
                        onClose={() => this.setState({ previewUrl: '' })}
                        data={[{ imgSrc: previewUrl, imgName: '' }]}
                    />
                ) : null}

                {isPreviewDialog ? (
                    <PreviewTemplateForQop
                        open={isPreviewDialog}
                        theme={proQopSetting.tempTheme || 'light'}
                        codeType={codeType}
                        onClose={() => this.setState({ isPreviewDialog: false })}
                    />
                ) : null}
            </Box>
        );
    }
}

export default QopSettingPage;
